export enum ServiceProviderType {
  PLUMBER = 'PLUMBER',
  ELECTRICIAN = 'ELECTRICIAN',
  HVAC = 'HVAC',
  HANDYMAN = 'HANDYMAN',
  CLEANER = 'CLEANER',
  GARDENER = 'GARDENER',
  PAINTER = 'PAINTER',
  LOCKSMITH = 'LOCKSMITH',
  ROOFER = 'ROOFER',
  ENERGY_SPECIALIST = 'ENERGY_SPECIALIST',
  GENERAL_CONTRACTOR = 'GENERAL_CONTRACTOR',
  OTHER = 'OTHER'
}

/**
 * i18n keys for the provider type.
 *
 * Shared so the screens cannot disagree. The add form translated these while
 * the detail page rendered the raw enum name ("OTHER") and the list rendered a
 * translated-but-uppercased value — the same provider read three different ways
 * depending on where you looked.
 */
export const SERVICE_PROVIDER_TYPE_LABEL_KEYS: Record<ServiceProviderType, string> = {
  [ServiceProviderType.PLUMBER]: 'provider.detail.types.plumber',
  [ServiceProviderType.ELECTRICIAN]: 'provider.detail.types.electrician',
  [ServiceProviderType.HVAC]: 'provider.detail.types.hvac',
  [ServiceProviderType.HANDYMAN]: 'provider.detail.types.handyman',
  [ServiceProviderType.CLEANER]: 'provider.detail.types.cleaner',
  [ServiceProviderType.GARDENER]: 'provider.detail.types.gardener',
  [ServiceProviderType.PAINTER]: 'provider.detail.types.painter',
  [ServiceProviderType.LOCKSMITH]: 'provider.detail.types.locksmith',
  [ServiceProviderType.ROOFER]: 'provider.detail.types.roofer',
  [ServiceProviderType.ENERGY_SPECIALIST]: 'provider.detail.types.energySpecialist',
  [ServiceProviderType.GENERAL_CONTRACTOR]: 'provider.detail.types.generalContractor',
  [ServiceProviderType.OTHER]: 'provider.detail.types.other'
};

export enum CommunicationPreference {
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
  SMS = 'SMS',
  BOTH = 'BOTH'
}

export enum ServiceProviderAvailability {
  WEEKDAYS = 'WEEKDAYS',
  WEEKENDS = 'WEEKENDS',
  EVENINGS = 'EVENINGS',
  EMERGENCY_24_7 = 'EMERGENCY_24_7',
  BY_APPOINTMENT = 'BY_APPOINTMENT'
}

export interface PropertyPreferences {
  preferredPropertyTypes?: string[]; // e.g., 'apartment', 'house', 'commercial'
  maximumProperties?: number; // Maximum number of properties they can handle
  preferredLocations?: string[]; // Geographic preferences
  excludedLocations?: string[]; // Areas they don't serve
  minimumJobValue?: number; // Minimum job value they accept
  notes?: string;
}

export interface CommunicationSettings {
  preferredMethod: CommunicationPreference;
  emergencyContact?: string; // Alternative contact for urgent matters
  responseTimeExpected?: number; // Expected response time in hours
  workingHours?: {
    start: string; // e.g., "08:00"
    end: string; // e.g., "17:00"
  };
  availableDays?: string[]; // e.g., ['monday', 'tuesday', 'wednesday']
}

export interface PerformanceMetrics {
  completedJobs: number;
  averageRating: number;
  onTimeCompletionRate: number; // Percentage (0-100)
  responseTimeAverage: number; // Average response time in hours
  customerSatisfactionScore: number; // 1-5 scale
  repeatCustomerRate: number; // Percentage (0-100)
  lastPerformanceUpdate: Date;
}

export interface AvailabilitySchedule {
  availability: ServiceProviderAvailability[];
  emergencyAvailable: boolean;
  leadTimeRequired: number; // Days of lead time required for booking
  maxJobsPerWeek?: number;
  currentlyAvailable: boolean;
  nextAvailableDate?: Date;
  unavailablePeriods?: {
    start: Date;
    end: Date;
    reason: string;
  }[];
}

/**
 * Claim status for service provider profiles
 * - unclaimed: Owner created entry, provider not yet invited or linked
 * - invited: Owner sent invitation, waiting for provider to accept
 * - claimed: Provider accepted invitation and linked their workspace
 */
export type ServiceProviderClaimStatus = 'unclaimed' | 'invited' | 'claimed';

/**
 * Banking coordinates for a provider/supplier. Optional and free-form by
 * design — the IBAN/BIC are validated only with the non-blocking soft
 * validators so an incomplete entry never prevents saving the carnet card.
 * Surfaced when recording an expense paid to this provider (Syndic workflow).
 */
export interface ProviderBankAccount {
  iban?: string;
  bic?: string;
  accountHolder?: string;
}

/**
 * The four languages the product ships.
 *
 * Deliberately NOT the legacy `CommunicationLanguage` enum from
 * `definitions/contact.ts`: that one predates German (fr/nl/en only) and is
 * consumed by exhaustive switches elsewhere, so widening it is a change with a
 * blast radius far beyond this one field.
 */
export type ProviderLanguage = 'fr' | 'nl' | 'en' | 'de';

export interface ServiceProvider {
  providerUID: string;
  name: string;
  type: ServiceProviderType;
  /**
   * The language this provider reads, when the owner recorded it (#388).
   *
   * Optional and never defaulted: a guessed language is worse than none,
   * because anything consuming it treats a present value as a fact about a
   * person. Mirrors `tenants/{id}.contacts[].preferredLanguage`, which the
   * cockpit already surfaces next to a tenant mail draft.
   *
   * Measured on proporio-dev before this field existed: 0 of 30 providers
   * carried any language at all, which is why the cockpit could not tell an
   * owner they were about to write French at a Dutch-speaking plumber.
   */
  preferredLanguage?: ProviderLanguage;
  contactInfo: {
    phone: string;
    email: string;
    address: string;
    /**
     * La personne à qui l'on parle dans l'entreprise (#645, A1). Sous
     * `contactInfo` à dessein : ce champ maître est verrouillé par les règles
     * une fois la fiche réclamée, la personne de contact l'est donc aussi.
     */
    contactPerson?: { firstName?: string; lastName?: string };
  };
  /**
   * The provider agreed to be reached on WhatsApp (#377).
   *
   * A flag on the SHEET, not one per contact: a provider record carries a flat
   * `contactInfo.phone`, no `contacts[]` array — which is precisely why the
   * consent basis `provider-opt-in` exists apart from `tenant-opt-in`
   * (`backend/functions/src/whatsapp/whatsappConsent.ts`).
   *
   * Opt-IN, never defaulted. The channel is billed per conversation and lands
   * on a personal messenger; holding a contractor's number is not their
   * agreement to be canvassed on it. `whatsappOptedOut` stays authoritative
   * over this — the guard tests it first, and no screen may work around it.
   *
   * Ticking it does not open the channel by itself: it fires
   * `onProviderWhatsappActivated`, which sends one welcome message carrying a
   * confirmation link. Until the provider opens that link, the possession gate
   * added in #399 refuses every other send.
   */
  whatsappOptIn?: boolean;
  /** The provider said STOP. Outranks `whatsappOptIn`, always. */
  whatsappOptedOut?: boolean;
  rating: number; // Overall rating (legacy - will use performanceMetrics.averageRating going forward)
  isActive: boolean;
  specialties: string[];
  notes: string;
  createdAt: Date;
  updatedAt: Date;

  // Enhanced fields for comprehensive task management
  propertyPreferences?: PropertyPreferences;
  communicationSettings?: CommunicationSettings;
  performanceMetrics?: PerformanceMetrics;
  availabilitySchedule?: AvailabilitySchedule;

  // Banking coordinates (used to pre-fill expense payments to this provider)
  bankAccount?: ProviderBankAccount;

  /** Download URL for the carnet logo (Firebase Storage). Optional. */
  logoUrl?: string;

  // Additional business information
  businessInfo?: {
    companyName?: string;
    licenseNumber?: string;
    insuranceDetails?: string;
    website?: string;
    yearsInBusiness?: number;
    certifications?: string[];
  };

  // Profile claiming/linking fields (for cross-workspace integration)
  claimStatus?: ServiceProviderClaimStatus;
  /**
   * Favori du carnet (#645, L1). Colonne du PROPRIÉTAIRE, hors du verrou.
   * Absent = favori : tout ce qui est dans le carnet y a été mis par lui.
   */
  isFavorite?: boolean;
  /** Première ouverture du lien d'invitation, posée par le serveur (#645, I3). */
  invitationOpenedAt?: Date;
  claimedByWorkspaceId?: string;    // Reference to provider's workspace when claimed
  claimedAt?: Date;                  // When the provider claimed this profile
  invitationSentAt?: Date;           // When the invitation was sent
  invitationId?: string;             // Reference to the invitation in provider_invitations collection
  isMasterProfile?: boolean;         // True when this is synced from the claimed workspace's profile
}

/**
 * Fields the PROVIDER owns once they have claimed their profile (#218).
 *
 * After `claimStatus === 'claimed'`, the provider's own workspace profile is the
 * source of truth and `syncProfileToLinkedOwners` pushes it into every linked
 * owner carnet. An owner writing these fields is not making a correction, it is
 * starting a fight with that sync: whoever writes last wins until the next
 * provider edit silently reverts it.
 *
 * The claim bookkeeping itself is in here too — it is set by the invitation
 * flow server-side and was never the client's to touch.
 *
 * Everything NOT listed stays the owner's: notes, tags, `isActive`, preferences,
 * ratings. Those describe the RELATIONSHIP, which is the owner's to record even
 * about a claimed provider.
 *
 * **`bankAccount` is deliberately NOT here.** The provider workspace profile has
 * no bank fields at all — `updateProviderProfile` never writes an IBAN and
 * `syncProfileToLinkedOwners` never pushes one. Locking it would freeze
 * whatever the owner typed with no way for anyone to correct it, including the
 * provider. It stays the owner's until the provider profile can actually carry
 * it; adding that is a feature, not a lock.
 *
 * The same list is enforced authoritatively in `firestore.rules` under
 * `match /service_providers/{providerId}` — keep the two in step.
 */
export const PROVIDER_MASTER_FIELDS = [
  'name',
  'contactInfo',
  'businessInfo',
  'logoUrl',
  'claimStatus',
  'claimedByWorkspaceId',
  'claimedAt',
  'isMasterProfile',
] as const;

/**
 * Which master fields an update would overwrite. Empty when the update only
 * touches the owner's own columns.
 *
 * Works on the flattened payload too: `buildProviderUpdatePayload` can emit
 * dotted keys such as `contactInfo.email`, and a prefix match is what catches
 * those — a plain `includes` would wave them straight through.
 */
export function masterFieldsTouchedBy(update: Record<string, unknown>): string[] {
  return Object.keys(update).filter((key) =>
    PROVIDER_MASTER_FIELDS.some(
      (field) => key === field || key.startsWith(`${field}.`),
    ),
  );
}

/** True once the provider owns their profile and the owner may no longer rewrite it. */
export function isClaimedProfile(
  provider: Pick<ServiceProvider, 'claimStatus'> | null | undefined,
): boolean {
  return provider?.claimStatus === 'claimed';
} 